// Links into the Browser, and the ones an external system can rely on.
//
//   /{database}/map/{guid}           its picture, with ?format=bpmn|svg|png|structure
//   /{database}/object/{guid}        the object in the Relationship Explorer
//   /{database}/model/{guid}         what /map/{guid} was called until 28 Sep 2026
//   /map/{guid}                      the same, in whichever database the reader is in
//   /object/{guid}                   the same
//   /model/{guid}                    the same
//
// `map` is the canonical address for the picture: it is what the app writes,
// what a reader copies and what "Open diagram" links to. `model` described
// neither the drawing nor the list of parts (product owner, 28 Sep 2026) and
// is kept for ever: every mail and ticket that carries one still opens the
// same object, query string and all - it redirects to the `map` address
// rather than being routed twice, so there is one shareable shape.
//
// The database-free shapes are for links written by someone who doesn't know
// (or care) which database the reader has open. They resolve to the reader's
// current database and move to the canonical link, query string and all.

// The shapes with no database in them, as react-router paths.
export const DATABASE_FREE_PATHS = Object.freeze(["/object/:id", "/map/:id", "/model/:id"]);

// The address of an object's picture. One place builds it, so renaming the
// slug again is one line here and the old one keeps redirecting.
export const mapPath = (database, guid) =>
  `${database ? `/${encodeURIComponent(database)}` : ""}/map/${encodeURIComponent(guid)}`;

// The database the reader is in: the one they switched to, else the one their
// account starts in. Private-mode browsers can refuse localStorage, which is
// not a reason to fail the link.
export const currentDatabase = (user) => {
  let stored = null;
  try {
    stored = window.localStorage.getItem("db");
  } catch (err) {
    stored = null;
  }
  const database = stored || user?.mainDatabase || "";
  return typeof database === "string" && database.trim() ? database.trim() : null;
};

// The database a link names, for the shapes that carry one. A link is the
// reader asking for a particular database, so it - not what they had open
// last - decides where they land, and it can move the workspace with it
// (utils/readingContext.js resolveContext). Anything else, including the
// console and the account pages, names no database and returns null.
const DATABASE_BEARING = /^\/([^/]+)\/(object|map|model|advance-search|external-link|not-found)(\/|$)/;

export const databaseInLink = (pathname) => {
  const path = typeof pathname === "string" ? pathname : "";
  const home = path.match(/^\/home\/([^/]+)\/?$/);
  const named = home || path.match(DATABASE_BEARING);
  if (!named) return null;
  try {
    return decodeURIComponent(named[1]) || null;
  } catch (err) {
    return named[1] || null;
  }
};

// The old slug for a picture, written into links before 28 Sep 2026. It is
// the same address under a clearer name, so a link that carries it lands on
// the canonical one in a single step rather than being redirected twice.
export const canonicalPath = (pathname) => {
  const path = typeof pathname === "string" ? pathname : "";
  return path.startsWith("/model/") ? `/map/${path.slice("/model/".length)}` : path;
};

// Where a database-free link goes, or null when the reader has no database to
// open it in (the "no database" card says so, rather than a blank page).
export const canonicalLink = ({ pathname, search } = {}, user) => {
  const database = currentDatabase(user);
  if (!database) return null;
  const query = typeof search === "string" ? search : "";
  return `/${encodeURIComponent(database)}${canonicalPath(pathname)}${query}`;
};
